/**
 * Static HTML Generator
 * Генерирует статический HTML из данных страницы
 */
import { renderToStaticMarkup } from 'react-dom/server';
import React from 'react';
import { sections } from '../sections';
import { getThemeCSS, getThemeFontsUrl } from '../lib/themes';
import config from '../config';

/**
 * Генерация HTML для одной секции
 * @param {Object} section - данные секции
 * @param {boolean} forHydration - добавлять ли обертку для гидрации
 * @param {number} index - индекс секции (для id)
 * @param {string} theme - ключ темы
 */
function renderSection(section, forHydration = false, index = 0, theme = 'default') {
  const sectionConfig = sections[section.type];
  if (!sectionConfig) {
    console.warn(`Unknown section type: ${section.type}`);
    return '';
  }

  const Component = sectionConfig.Component;
  const props = {
    data: section.content,
    styles: section.styles,
    theme
  };

  try {
    const element = React.createElement(Component, props);
    const html = renderToStaticMarkup(element);

    if (forHydration) {
      // Оборачиваем в контейнер для гидрации с id для навигации
      // ID по индексу чтобы совпадало с #section-0, #section-1... из редактора
      // Escape: < для HTML, ' для атрибута в одинарных кавычках
      const propsJson = JSON.stringify(props)
        .replace(/</g, '\\u003c')
        .replace(/'/g, '\\u0027');
      return `<div id="section-${index}" data-hydrate="${section.type}" data-props='${propsJson}'>${html}</div>`;
    }

    return html;
  } catch (error) {
    console.error(`Error rendering section ${section.type}:`, error);
    return `<!-- Error rendering section: ${section.type} -->`;
  }
}

/**
 * Генерация полного HTML документа
 * @param {Object} pageData - данные страницы
 * @param {Object} options - опции генерации
 * @param {boolean} options.enableHydration - включить гидрацию (React интерактивность)
 */
export function generateHTML(pageData, options = {}) {
  const {
    title = 'Untitled Page',
    description = '',
    favicon = '',
    ogImage = '',
    siteUrl = '',
    customHead = '',
    customScripts = '',
    enableHydration = true,
    theme = 'default',
  } = options;

  // URL hydrate bundle берём из env или используем относительный путь к основному сайту
  const appBaseUrl = import.meta.env.VITE_APP_URL || '';
  const hydrateUrl = enableHydration ? `${appBaseUrl}/hydrate.js` : '';

  // Рендерим все секции
  let sectionsHTML = (pageData.sections || [])
    .map((section, index) => renderSection(section, enableHydration, index, theme))
    .join('\n');

  // SEO: Убираем hidden с FAQ контента, чтобы поисковики видели ответы
  sectionsHTML = sectionsHTML.replace(
    /(<div[^>]*data-faq-content[^>]*class="[^"]*)\s*hidden([^"]*")/g,
    '$1$2'
  );

  // Скрипты для гидрации (React включен в bundle)
  const hydrationScripts = enableHydration ? `
  <!-- Hydration Bundle (includes React) -->
  <script src="${hydrateUrl}"></script>
` : '';

  // Google Fonts для темы
  const fontsUrl = getThemeFontsUrl(theme);
  const fontsLink = fontsUrl ? `<link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">` : '';

  // Open Graph meta tags
  const ogTags = `
  <!-- Open Graph / Social Media -->
  <meta property="og:type" content="website">
  <meta property="og:title" content="${escapeHtml(title)}">
  ${description ? `<meta property="og:description" content="${escapeHtml(description)}">` : ''}
  ${siteUrl ? `<meta property="og:url" content="${escapeHtml(siteUrl)}">` : ''}
  ${ogImage ? `<meta property="og:image" content="${escapeHtml(ogImage)}">` : ''}
  ${ogImage ? `<meta property="og:image:width" content="1200">` : ''}
  ${ogImage ? `<meta property="og:image:height" content="630">` : ''}

  <!-- Twitter Card -->
  <meta name="twitter:card" content="summary_large_image">
  <meta name="twitter:title" content="${escapeHtml(title)}">
  ${description ? `<meta name="twitter:description" content="${escapeHtml(description)}">` : ''}
  ${ogImage ? `<meta name="twitter:image" content="${escapeHtml(ogImage)}">` : ''}`;

  // Собираем HTML документ
  const html = `<!DOCTYPE html>
<html lang="ru">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(title)}</title>
  ${description ? `<meta name="description" content="${escapeHtml(description)}">` : ''}
  ${favicon ? `<link rel="icon" href="${escapeHtml(favicon)}">` : ''}
  ${ogTags}

  ${fontsLink}

  <!-- App CSS (compiled Tailwind) -->
  <link rel="stylesheet" href="${appBaseUrl}/export.css">

  <!-- Theme CSS Variables -->
  <style>
    ${getThemeCSS(theme)}
  </style>

  ${customHead}
</head>
<body>
  ${sectionsHTML}

  <!-- Created with ${config.appName} badge -->
  <div class="fixed bottom-4 right-4 z-50">
    <a
      href="${config.appUrl}"
      target="_blank"
      rel="noopener noreferrer"
      class="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-full shadow-lg hover:shadow-xl transition-shadow text-sm"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
      </svg>
      <span>Создано в <strong>${config.appName}</strong></span>
    </a>
  </div>

  ${customScripts}
${hydrationScripts}
</body>
</html>`;

  return html;
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text) {
  if (!text) return '';
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return String(text).replace(/[&<>"']/g, m => map[m]);
}

export default { generateHTML };
